import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SignOut, SignOutOnError } from '@platform-frontend/account';
import { OneTimePurchaseBookingStatus, SubscriptionBookingStatus } from '@platform-frontend/checkout-drop-in';
import {
  Asset,
  AssetOwnershipStatus,
  CancelSubscriptionParameters,
  CancelSubscriptionService,
  LicenseType,
  LicenseValidityContext,
  OneTimePurchase,
  OneTimePurchaseDetails,
  PricingInformation,
  Product,
  ProductIdentifier,
  SaasApiKeyService,
  SerializedAssetService,
  SetSaasApiKey,
  Subscription,
  SubscriptionDetails,
  SubscriptionEulaUpdateService,
  SubscriptionPriceUpdateService,
  TravelTicketRoute
} from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import {
  InvalidStateError,
  PlannedError,
  ShowWarningNotification,
  convertToHttpParams,
  deepEqual,
  throwAndHandleError,
  throwIfNullish
} from '@platform-frontend/util';
import { Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { FlowState } from '../flow';
import { MSG_UPDATE_BELONGED_SOLUTIONS_DEFAULT } from '../notification-messages';
import { ProductState } from '../product';
import { CancelSubscription, ConfirmSubscriptionEulaUpdate, ConfirmSubscriptionPriceUpdate, FetchAssets } from './asset.actions';
import { AssetDetails } from './model/asset-details';
import { AssetStateModel } from './model/asset-state-model';
import { AssetStatusWithProduct } from './model/asset-status-with-product';
import { SerializedAsset } from './model/serialized-asset';

const DEFAULT_STATE: AssetStateModel = {
  assets: null
};

@State<AssetStateModel>({
  name: 'asset',
  defaults: DEFAULT_STATE
})
@Injectable()
export class AssetState {
  constructor(
    private cancelSubscriptionService: CancelSubscriptionService,
    private http: HttpClient,
    private saasApiKeyService: SaasApiKeyService,
    private serializedAssetService: SerializedAssetService,
    private subscriptionEulaUpdateService: SubscriptionEulaUpdateService,
    private subscriptionPriceUpdateService: SubscriptionPriceUpdateService,
    private store: Store
  ) {}

  product$: Observable<Product | null> = this.store.select(ProductState.product);
  contractId$: Observable<string | null> = this.store.select(FlowState.contractId);
  productIdentifier$: Observable<ProductIdentifier | null> = this.store.select(ProductState.productIdentifier);
  licenseTypes$: Observable<LicenseType[]> = this.store.select(ProductState.licenseTypes);

  private static isSubscriptionType(asset: Asset | null): asset is Subscription {
    return asset?.type === 'SUBSCRIPTION';
  }

  private static isOneTimePurchaseType(asset: Asset | null): asset is OneTimePurchase {
    return asset?.type === 'ONE_TIME_PURCHASE';
  }

  @Selector()
  static assets(state: AssetStateModel): Asset[] | null {
    return state.assets;
  }

  @Selector([ProductState.product, FlowState.contractId])
  static asset(state: AssetStateModel, product: Product | null, contractId: string | null): Asset | null {
    const licenseTypeIds = product?.licenseTypes.map(licenseType => licenseType.licenseTypeId) ?? [];
    const productIdentifier = product?.productIdentifier;

    return (
      state.assets?.find(
        asset =>
          deepEqual(asset.product.productIdentifier, productIdentifier) &&
          licenseTypeIds.includes(asset.product.licenseTypes[0].licenseTypeId) &&
          (contractId === null ? true : asset.contractId === contractId)
      ) ?? null
    );
  }

  @Selector([AssetState.asset])
  static contractId(state: AssetStateModel, asset: Asset | null): string | null {
    return asset?.contractId ?? null;
  }

  @Selector([AssetState.asset])
  static hasAsset(state: AssetStateModel, asset: Asset | null): boolean {
    return asset !== null;
  }

  @Selector([AssetState.asset])
  static apiKeys(state: AssetStateModel, asset: Asset | null): string[] {
    return asset?.apiKeys ?? [];
  }

  @Selector([AssetState.assets])
  static subscriptions(state: AssetStateModel, assets: Asset[] | null): Subscription[] | null {
    return assets?.filter(this.isSubscriptionType) ?? null;
  }

  @Selector([AssetState.asset])
  static subscription(state: AssetStateModel, asset: Asset | null): Subscription | null {
    return this.isSubscriptionType(asset) ? asset : null;
  }

  @Selector([AssetState.assets])
  static oneTimePurchases(state: AssetStateModel, assets: Asset[] | null): OneTimePurchase[] | null {
    return assets?.filter(this.isOneTimePurchaseType) ?? null;
  }

  @Selector([AssetState.asset])
  static oneTimePurchase(state: AssetStateModel, asset: Asset | null): OneTimePurchase | null {
    return this.isOneTimePurchaseType(asset) ? asset : null;
  }

  @Selector([AssetState.asset])
  static assetDetails(state: AssetStateModel, asset: Asset | null): AssetDetails | null {
    return this.isSubscriptionType(asset) ? asset.subscriptionDetails : asset?.oneTimePurchaseDetails ?? null;
  }

  @Selector([AssetState.assetDetails])
  static pricingInformation(state: AssetStateModel, assetDetails: AssetDetails | null): PricingInformation | null {
    return assetDetails?.pricingInformation ?? null;
  }

  @Selector([AssetState.assetDetails])
  static revokableUntil(state: AssetStateModel, assetDetails: AssetDetails | null): Date | null {
    return assetDetails?.revokableUntil ?? null;
  }

  @Selector([AssetState.assetDetails])
  static purchaseDate(state: AssetStateModel, assetDetails: AssetDetails | null): Date | null {
    return assetDetails?.purchaseDate ?? null;
  }

  @Selector([AssetState.subscription])
  static subscriptionDetails(state: AssetStateModel, subscription: Subscription | null): SubscriptionDetails | null {
    return subscription?.subscriptionDetails ?? null;
  }

  @Selector([AssetState.subscriptionDetails])
  static confirmedPricingInformation(state: AssetStateModel, subscriptionDetails: SubscriptionDetails | null): PricingInformation | null {
    return subscriptionDetails?.confirmedPricingInformation ?? null;
  }

  @Selector([AssetState.subscriptionDetails])
  static confirmedTermsVersion(state: AssetStateModel, subscriptionDetails: SubscriptionDetails | null): string | null {
    return subscriptionDetails?.confirmedTermsVersion ?? null;
  }

  @Selector([AssetState.subscriptionDetails])
  static subscriptionBookingStatus(state: AssetStateModel, subscriptionDetails: SubscriptionDetails | null): SubscriptionBookingStatus | null {
    return subscriptionDetails?.bookingStatus ?? null;
  }

  @Selector([AssetState.subscriptionDetails])
  static endOfPeriod(state: AssetStateModel, subscriptionDetails: SubscriptionDetails | null): Date | null {
    return subscriptionDetails?.endOfPeriod ?? null;
  }

  @Selector([AssetState.subscriptionDetails])
  static endOfNoticePeriod(state: AssetStateModel, subscriptionDetails: SubscriptionDetails | null): Date | null {
    return subscriptionDetails?.endOfNoticePeriod ?? null;
  }

  @Selector([AssetState.subscriptionDetails])
  static terminationDate(state: AssetStateModel, subscriptionDetails: SubscriptionDetails | null): Date | null {
    return subscriptionDetails?.terminationDate ?? null;
  }

  @Selector([AssetState.oneTimePurchase])
  static oneTimePurchaseDetails(state: AssetStateModel, oneTimePurchase: OneTimePurchase | null): OneTimePurchaseDetails | null {
    return oneTimePurchase?.oneTimePurchaseDetails ?? null;
  }

  @Selector([AssetState.oneTimePurchaseDetails])
  static oneTimePurchaseBookingStatus(state: AssetStateModel, oneTimePurchaseDetails: OneTimePurchaseDetails | null): OneTimePurchaseBookingStatus | null {
    return oneTimePurchaseDetails?.bookingStatus ?? null;
  }

  @Selector([AssetState.asset])
  static product(state: AssetStateModel, asset: Asset | null): Product | null {
    return asset?.product ?? null;
  }

  @Selector([AssetState.product])
  static travelTicketRoute(state: AssetStateModel, product: Product | null): TravelTicketRoute | null {
    return product?.productCategoryUpdate?.context?.category === 'TRAVEL_TICKET' ? product.productCategoryUpdate.context.route : null;
  }

  @Selector([AssetState.product])
  static productVariant(state: AssetStateModel, product: Product | null): string | null {
    return product?.productLocalization?.productVariant ?? null;
  }

  @Selector([AssetState.product])
  static licenseType(state: AssetStateModel, product: Product | null): LicenseType | null {
    return product?.licenseTypes[0] ?? null;
  }

  @Selector([AssetState.licenseType])
  static licenseValidity(state: AssetStateModel, licenseType: LicenseType | null): LicenseValidityContext | null {
    return licenseType?.validityUpdate.context ?? null;
  }

  @Selector([AssetState.subscriptionBookingStatus])
  static isOngoingSubscription(state: AssetStateModel, bookingStatus: SubscriptionBookingStatus | null): boolean {
    return bookingStatus === 'SUBSCRIBED' || bookingStatus === 'BOOKED';
  }

  @Selector([AssetState.subscriptionBookingStatus])
  static isExpiringSubscription(state: AssetStateModel, bookingStatus: SubscriptionBookingStatus | null): boolean {
    return bookingStatus === 'EXPIRING';
  }

  @Selector([AssetState.isOngoingSubscription, AssetState.isExpiringSubscription, AssetState.terminationDate, AssetState.endOfPeriod])
  static outstandingTransactions(
    state: AssetStateModel,
    isOngoingSubscription: boolean,
    isExpiringSubscription: boolean,
    terminationDate: Date | null,
    endOfPeriod: Date | null
  ): boolean {
    return isOngoingSubscription || (isExpiringSubscription && terminationDate !== null && endOfPeriod !== null && terminationDate > endOfPeriod);
  }

  @Selector([AssetState.isExpiringSubscription, FlowState.isOrderToBeManaged, ProductState.isMultipleCheckoutAllowed, AssetState.hasAsset])
  static isPurchaseAllowed(
    state: AssetStateModel,
    isExpiringSubscription: boolean,
    isOrderToBeManaged: boolean,
    isMultipleCheckoutAllowed: boolean,
    hasAsset: boolean
  ): boolean {
    return isExpiringSubscription || ((!hasAsset || isMultipleCheckoutAllowed) && !isOrderToBeManaged);
  }

  @Selector([AssetState.subscription])
  static isSubscription(state: AssetStateModel, subscription: OneTimePurchase | null): boolean {
    return subscription !== null;
  }

  @Selector([AssetState.oneTimePurchase])
  static isOneTimePurchase(state: AssetStateModel, oneTimePurchase: OneTimePurchase | null): boolean {
    return oneTimePurchase !== null;
  }

  @Selector([AssetState.assets, AssetState.asset, AssetState.isOneTimePurchase])
  static quantitySum(state: AssetStateModel, assets: Asset[] | null, asset: Asset | null, isOneTimePurchase: boolean): number {
    let quantitySum: number = 0;
    if (isOneTimePurchase) {
      assets
        ?.filter(assetEntry => deepEqual(assetEntry.product.productIdentifier, asset?.product.productIdentifier) && assetEntry.type === 'ONE_TIME_PURCHASE')
        .forEach(assetEntry => (quantitySum += assetEntry.quantity));
    }
    return quantitySum;
  }

  @Selector([FlowState.isOrderToBeManaged, AssetState.subscriptionBookingStatus, AssetState.oneTimePurchaseBookingStatus])
  static assetOwnershipStatus(
    state: AssetStateModel,
    isOrderToBeManaged: boolean,
    bookingStatus: SubscriptionBookingStatus | null,
    oneTimePurchaseBookingStatus: OneTimePurchaseBookingStatus | null
  ): AssetOwnershipStatus | null {
    return oneTimePurchaseBookingStatus ?? (isOrderToBeManaged ? bookingStatus : null);
  }

  @Selector([AssetState.subscriptions, AssetState.oneTimePurchases])
  static assetStatusWithProduct(
    state: AssetStateModel,
    subscriptions: Subscription[] | null,
    oneTimePurchases: OneTimePurchase[] | null
  ): AssetStatusWithProduct[] {
    const subscriptionAssetStatus =
      subscriptions?.map<AssetStatusWithProduct>(subscription => ({
        product: subscription.product,
        assetStatus: {
          type: 'SUBSCRIPTION',
          contractId: subscription.contractId,
          bookingStatus: subscription.subscriptionDetails.bookingStatus
        }
      })) ?? [];
    const oneTimePurchaseAssetStatus =
      oneTimePurchases?.map<AssetStatusWithProduct>(oneTimePurchase => ({
        product: oneTimePurchase.product,
        assetStatus: {
          type: 'ONE_TIME_PURCHASE',
          bookingStatus: oneTimePurchase.oneTimePurchaseDetails.bookingStatus,
          contractId: oneTimePurchase.contractId
        }
      })) ?? [];
    return subscriptionAssetStatus.concat(oneTimePurchaseAssetStatus);
  }

  @Action(SignOut)
  cleanState(ctx: StateContext<AssetStateModel>): void {
    ctx.patchState(DEFAULT_STATE);
  }

  @Action(FetchAssets)
  fetchSubscriptions(ctx: StateContext<AssetStateModel>): Observable<unknown> {
    return this.http.get<SerializedAsset[]>(PLATFORM_ENVIRONMENT.apiUrl + '/assets/all', { params: convertToHttpParams({ activeOnly: true }) }).pipe(
      map(assets => assets.map(asset => this.deserializeAsset(asset))),
      tap(assets => ctx.patchState({ assets })),
      catchError(() =>
        throwAndHandleError(new SignOutOnError(new ShowWarningNotification(MSG_UPDATE_BELONGED_SOLUTIONS_DEFAULT)), new PlannedError('FETCH_BELONGINGS_FAILED'))
      )
    );
  }

  private deserializeAsset(serializedAsset: SerializedAsset): Asset {
    return serializedAsset.type === 'SUBSCRIPTION'
      ? this.serializedAssetService.deserializeSubscription(serializedAsset)
      : this.serializedAssetService.deserializeOneTimePurchase(serializedAsset);
  }

  @Action(CancelSubscription)
  cancelSubscription(ctx: StateContext<AssetStateModel>): Observable<void> {
    return this.buildCancelSubscriptionParams(ctx.getState()).pipe(
      switchMap(params => this.cancelSubscriptionService.cancelSubscription(params)),
      switchMap(() => ctx.dispatch(new FetchAssets()))
    );
  }

  private buildCancelSubscriptionParams(state: AssetStateModel): Observable<CancelSubscriptionParameters> {
    return this.getSubscription(state).pipe(
      switchMap(subscription =>
        subscription && subscription.subscriptionDetails && subscription.contractId
          ? of({
              productIdentifier: subscription.product.productIdentifier,
              contractId: subscription.contractId,
              endOfPeriod: subscription.subscriptionDetails.endOfPeriod.toISOString(),
              terminationDate: subscription.subscriptionDetails.terminationDate.toISOString()
            })
          : throwError(() => new InvalidStateError('Cancel subscription parameters not available'))
      )
    );
  }

  @Action(ConfirmSubscriptionPriceUpdate)
  confirmSubscriptionPriceUpdate(ctx: StateContext<AssetStateModel>): Observable<unknown> {
    return this.getSubscription(ctx.getState()).pipe(
      throwIfNullish(() => new InvalidStateError('No subscription available')),
      tap(subscription => this.confirmPriceUpdateLocally(ctx, subscription)),
      switchMap(subscription => this.subscriptionPriceUpdateService.confirmPriceUpdates([subscription.product.licenseTypes[0].licenseTypeId]))
    );
  }

  private confirmPriceUpdateLocally(ctx: StateContext<AssetStateModel>, subscription: Subscription): void {
    this.updateSubscriptionWithLicenseTypeId(ctx, subscription, (subscription: Subscription) => {
      return {
        ...subscription,
        subscriptionDetails: { ...subscription.subscriptionDetails, confirmedPricingInformation: subscription.subscriptionDetails.pricingInformation }
      };
    });
  }

  @Action(ConfirmSubscriptionEulaUpdate)
  confirmSubscriptionEulaUpdate(ctx: StateContext<AssetStateModel>): Observable<unknown> {
    return this.getSubscription(ctx.getState()).pipe(
      throwIfNullish(() => new InvalidStateError('No subscription available')),
      tap(subscription => this.confirmEulaUpdateLocally(ctx, subscription)),
      switchMap(subscription => this.subscriptionEulaUpdateService.confirmEulaUpdates([subscription.product.licenseTypes[0].licenseTypeId]))
    );
  }

  @Action(SetSaasApiKey)
  setSaasApiKey(ctx: StateContext<AssetStateModel>, { licenseType, saasApiKey }: SetSaasApiKey): void {
    const assets = ctx.getState().assets;
    if (assets) {
      ctx.patchState({
        assets: this.saasApiKeyService.updateAssetArray<Asset>(assets, licenseType, saasApiKey)
      });
    }
  }

  private confirmEulaUpdateLocally(ctx: StateContext<AssetStateModel>, subscription: Subscription): void {
    const termsVersion = subscription.product.productLocalization.termsVersion ?? null;
    this.updateSubscriptionWithLicenseTypeId(ctx, subscription, (subscription: Subscription) => {
      return { ...subscription, subscriptionDetails: { ...subscription.subscriptionDetails, confirmedTermsVersion: termsVersion } };
    });
  }

  private getSubscription(state: AssetStateModel): Observable<Subscription | null> {
    return combineLatest([this.product$, this.contractId$]).pipe(
      take(1),
      map(([product, contractId]) => AssetState.subscription(state, AssetState.asset(state, product, contractId)))
    );
  }

  private updateSubscriptionWithLicenseTypeId(
    ctx: StateContext<AssetStateModel>,
    subscription: Subscription,
    updateSubscription: (subscription: Subscription) => Subscription
  ): void {
    const assets = ctx.getState().assets;
    const licenseTypeId = subscription.product.licenseTypes[0].licenseTypeId;
    if (!assets) {
      throw new InvalidStateError(`Subscription with license type id ${licenseTypeId} does not exist`);
    }
    const index = assets.findIndex(asset => asset.product.licenseTypes[0].licenseTypeId === licenseTypeId);

    if (index > -1) {
      ctx.patchState({ assets: [...assets.slice(0, index), updateSubscription(subscription), ...assets.slice(index + 1)] });
    } else {
      throw new InvalidStateError(`Subscription with license type id ${licenseTypeId} does not exist`);
    }
  }
}
